import { useRef, useState } from 'react';

import { useQuery } from '@tanstack/react-query';
import { FileText, ImagePlus, Send, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { whatsappTemplatesApi } from '@/lib/api/whatsapp-templates.api';

import type { TemplateComponent, WhatsAppTemplate } from '@/lib/api/whatsapp-templates.api';

interface MessageInputProps {
  onSend: (content: string) => void;
  onSendTemplate?: (
    templateName: string,
    language: string,
    components?: Array<Record<string, unknown>>,
    renderedText?: string,
  ) => void;
  onSendImage?: (file: File) => void;
  disabled?: boolean;
  channel?: string;
}

const getComponent = (
  template: WhatsAppTemplate,
  type: 'HEADER' | 'BODY',
): TemplateComponent | undefined => {
  const components = template.components;
  if (!Array.isArray(components)) return undefined;
  return (components as TemplateComponent[]).find((c) => c?.type === type);
};

/** Placeholder keys in appearance order: {{1}} {{2}} … or {{first_name}} …  */
const extractPlaceholders = (text: string): string[] => {
  const keys: string[] = [];
  for (const match of text.matchAll(/\{\{\s*([\w.]+)\s*\}\}/g)) {
    if (!keys.includes(match[1])) keys.push(match[1]);
  }
  const allNumeric = keys.every((k) => /^\d+$/.test(k));
  return allNumeric ? keys.sort((a, b) => Number(a) - Number(b)) : keys;
};

const renderText = (text: string, values: Record<string, string>): string =>
  text.replace(/\{\{\s*([\w.]+)\s*\}\}/g, (_, key: string) => values[key] ?? `{{${key}}}`);

/** Header {{1}} and body {{1}} are independent params — namespace the state keys. */
const scoped = (section: 'header' | 'body', values: Record<string, string>) =>
  Object.fromEntries(
    Object.entries(values)
      .filter(([k]) => k.startsWith(`${section}:`))
      .map(([k, v]) => [k.slice(section.length + 1), v]),
  );

const buildParameters = (keys: string[], values: Record<string, string>) => {
  const positional = keys.every((k) => /^\d+$/.test(k));
  return keys.map((key) =>
    positional
      ? { type: 'text', text: (values[key] ?? '').trim() }
      : { type: 'text', parameter_name: key, text: (values[key] ?? '').trim() },
  );
};

export const MessageInput = ({
  onSend,
  onSendTemplate,
  onSendImage,
  disabled,
  channel,
}: MessageInputProps) => {
  const [value, setValue] = useState('');
  const [showTemplates, setShowTemplates] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [variables, setVariables] = useState<Record<string, string>>({});
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { data: templates } = useQuery({
    queryKey: ['whatsapp-templates'],
    queryFn: () => whatsappTemplatesApi.list(),
    enabled: showTemplates,
  });

  const approvedTemplates = templates?.filter((t) => t.status === 'APPROVED') ?? [];
  const template = approvedTemplates.find((t) => t.name === selectedTemplate);
  const headerComponent = template ? getComponent(template, 'HEADER') : undefined;
  const bodyComponent = template ? getComponent(template, 'BODY') : undefined;
  // Media headers (IMAGE/VIDEO/DOCUMENT) need an asset link we can't collect here yet.
  const hasMediaHeader = !!headerComponent?.format && headerComponent.format !== 'TEXT';
  const headerText = !hasMediaHeader ? (headerComponent?.text ?? '') : '';
  const bodyText = bodyComponent?.text ?? '';
  const sections = [
    {
      section: 'header' as const,
      label: 'Header',
      keys: extractPlaceholders(headerText),
      examples: headerComponent?.example?.header_text ?? [],
    },
    {
      section: 'body' as const,
      label: 'Body',
      keys: extractPlaceholders(bodyText),
      examples: bodyComponent?.example?.body_text?.[0] ?? [],
    },
  ].filter((s) => s.keys.length > 0);
  const allFilled = sections.every((s) =>
    s.keys.every((key) => variables[`${s.section}:${key}`]?.trim()),
  );

  const closeTemplates = () => {
    setShowTemplates(false);
    setSelectedTemplate('');
    setVariables({});
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = value.trim();
    if (!trimmed) return;
    onSend(trimmed);
    setValue('');
  };

  const handleSendTemplate = () => {
    if (!template || !onSendTemplate || !allFilled || hasMediaHeader) return;

    const headerValues = scoped('header', variables);
    const bodyValues = scoped('body', variables);
    const componentList: Array<Record<string, unknown>> = [];
    const headerKeys = extractPlaceholders(headerText);
    const bodyKeys = extractPlaceholders(bodyText);
    if (headerKeys.length > 0) {
      componentList.push({ type: 'header', parameters: buildParameters(headerKeys, headerValues) });
    }
    if (bodyKeys.length > 0) {
      componentList.push({ type: 'body', parameters: buildParameters(bodyKeys, bodyValues) });
    }

    const renderedParts = [
      headerText ? renderText(headerText, headerValues) : '',
      bodyText ? renderText(bodyText, bodyValues) : '',
    ].filter(Boolean);
    const renderedText = renderedParts.length > 0 ? renderedParts.join('\n') : undefined;

    onSendTemplate(
      template.name,
      template.language ?? 'en_US',
      componentList.length > 0 ? componentList : undefined,
      renderedText,
    );
    closeTemplates();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && onSendImage) {
      onSendImage(file);
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="border-t">
      {showTemplates && (
        <div className="bg-muted/30 space-y-2 border-b px-3 py-2.5">
          <div className="flex items-center gap-2">
            <Select
              value={selectedTemplate}
              onValueChange={(name) => {
                setSelectedTemplate(name);
                setVariables({});
              }}
            >
              <SelectTrigger className="h-8 flex-1 text-xs">
                <SelectValue placeholder="Select a template..." />
              </SelectTrigger>
              <SelectContent>
                {approvedTemplates.map((t) => (
                  <SelectItem key={t.id} value={t.name}>
                    {t.name} ({t.language})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              size="sm"
              className="h-8"
              onClick={handleSendTemplate}
              disabled={!selectedTemplate || !allFilled || hasMediaHeader || disabled}
            >
              <Send className="mr-1 h-3.5 w-3.5" />
              Send
            </Button>
            <Button
              size="icon"
              variant="ghost"
              className="text-muted-foreground h-8 w-8"
              title="Close templates"
              onClick={closeTemplates}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>

          {template && hasMediaHeader && (
            <p className="text-destructive text-xs">
              This template has a {headerComponent?.format?.toLowerCase()} header, which is not
              supported here yet — pick a text-only template.
            </p>
          )}

          {template &&
            !hasMediaHeader &&
            sections.map((s) => (
              <div key={s.section} className="space-y-1.5">
                {sections.length > 1 && (
                  <p className="text-muted-foreground text-[10px] font-semibold tracking-wide uppercase">
                    {s.label} variables
                  </p>
                )}
                <div className="grid gap-1.5 sm:grid-cols-2">
                  {s.keys.map((key, i) => {
                    const stateKey = `${s.section}:${key}`;
                    return (
                      <div key={stateKey} className="flex items-center gap-1.5">
                        <span className="bg-muted text-muted-foreground shrink-0 rounded px-1.5 py-0.5 font-mono text-[10px]">
                          {`{{${key}}}`}
                        </span>
                        <Input
                          className="h-7 text-xs"
                          placeholder={s.examples[i] ?? `Value for {{${key}}}`}
                          value={variables[stateKey] ?? ''}
                          onChange={(e) =>
                            setVariables((v) => ({ ...v, [stateKey]: e.target.value }))
                          }
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}

          {template && !hasMediaHeader && (headerText || bodyText) && (
            <div className="text-muted-foreground bg-background/60 space-y-1 rounded-md border px-2.5 py-1.5 text-xs leading-relaxed whitespace-pre-wrap">
              {headerText && (
                <p className="text-foreground/80 font-semibold">
                  {renderText(headerText, scoped('header', variables))}
                </p>
              )}
              {bodyText && <p>{renderText(bodyText, scoped('body', variables))}</p>}
            </div>
          )}
        </div>
      )}
      <form onSubmit={handleSubmit} className="flex items-center gap-1.5 px-3 py-2.5">
        {onSendTemplate && (
          <Button
            type="button"
            size="icon"
            variant="ghost"
            title="Send a template"
            onClick={() => (showTemplates ? closeTemplates() : setShowTemplates(true))}
            className="text-muted-foreground h-8 w-8 shrink-0"
          >
            <FileText className="h-4 w-4" />
          </Button>
        )}
        {onSendImage && (
          <>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              className="hidden"
            />
            <Button
              type="button"
              size="icon"
              variant="ghost"
              title="Send an image"
              onClick={() => fileInputRef.current?.click()}
              disabled={disabled}
              className="text-muted-foreground h-8 w-8 shrink-0"
            >
              <ImagePlus className="h-4 w-4" />
            </Button>
          </>
        )}
        <Input
          value={value}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setValue(e.target.value)}
          placeholder={channel === 'EMAIL' ? 'Type an email message...' : 'Type a message...'}
          disabled={disabled}
          className="h-9 flex-1"
        />
        <Button type="submit" size="icon" className="h-9 w-9" disabled={disabled || !value.trim()}>
          <Send className="h-4 w-4" />
        </Button>
      </form>
    </div>
  );
};
